import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

function createPdfCalendarHtml(rows, startDate, endDate, titleText, type) {
  const start = moment(startDate, "YYYY-MM-DD");
  const end = moment(endDate, "YYYY-MM-DD");
  const isGregorianType = type === "gregorian";
  const rowMap = {};
  rows.forEach((row) => {
    rowMap[row.gregorianDate] = row;
  });

  const gridStart = start.clone().startOf("week");
  const gridEnd = end.clone().endOf("week");
  const weekdayHeaders = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const wrapper = document.createElement("div");
  wrapper.style.width = "1400px";
  wrapper.style.padding = "16px";
  wrapper.style.background = "#ffffff";
  //  wrapper.setAttribute("dir", "ltr");
  wrapper.style.fontFamily = "Arial, sans-serif";
  wrapper.style.color = "#1f2937";

  const title = document.createElement("div");
  title.style.display = "flex";
  title.style.flexDirection = "column";
  title.style.justifyContent = "center";
  title.style.alignItems = "center";
  title.style.textAlign = "center";
  title.style.fontWeight = "700";
  title.style.marginBottom = "12px";
  title.innerHTML = titleText;
  wrapper.appendChild(title);

  const rangeText = document.createElement("div");
  rangeText.style.fontSize = "16px";
  rangeText.style.marginBottom = "12px";
  wrapper.appendChild(rangeText);

  const table = document.createElement("table");
  table.style.width = "100%";
  table.style.borderCollapse = "collapse";
  table.style.tableLayout = "fixed";
  table.style.fontSize = "16px";
  const thead = document.createElement("thead");
  const headTr = document.createElement("tr");
  weekdayHeaders.forEach((label) => {
    const th = document.createElement("th");
    th.innerText = label;
    th.style.border = "2px solid #4a90e2";
    th.style.padding = "8px";
    th.style.background = "#ffffff";
    th.style.color = "#111827";
    th.style.fontWeight = "700";
    headTr.appendChild(th);
  });
  thead.appendChild(headTr);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  let cursor = gridStart.clone();
  while (cursor.isSameOrBefore(gridEnd, "day")) {
    const tr = document.createElement("tr");
    for (let i = 0; i < 7; i += 1) {
      const cellDate = cursor.clone();
      const isSaturday = cellDate.day() === 6;
      const td = document.createElement("td");
      td.style.border = "1px solid #4a90e2";
      td.style.height = "130px";
      td.style.padding = "6px";
      td.style.verticalAlign = "top";
      td.style.position = "relative";
      td.setAttribute("dir", "rtl");
      const iso = cellDate.format("YYYY-MM-DD");
      const row = rowMap[iso];
      const inRange =
        cellDate.isSameOrAfter(start, "day") &&
        cellDate.isSameOrBefore(end, "day");

      if (!inRange) {
        td.style.background = "#fafafa";
        td.style.color = "#999999";
      } else if ((row && row.holiday) || isSaturday) {
        td.style.background = row.bgColor;
      }

      if (row) {
        const dayNumber = document.createElement("div");
        if (isGregorianType) {
          dayNumber.innerText = String(row.gregorianDay);
        } else {
          const hebrewDateParts = String(row.hebrewDate || "")
            .trim()
            .split(" ");
          dayNumber.innerText = hebrewDateParts[0] ? hebrewDateParts[0] : "";
        }
        dayNumber.style.fontSize = "40px";
        dayNumber.style.lineHeight = "40px";
        dayNumber.style.color = "#1e88e5";
        dayNumber.style.fontWeight = "700";
        td.appendChild(dayNumber);

        const hebDate = document.createElement("div");
        if (isGregorianType) {
          hebDate.innerText = row.hebrewDate;
        } else {
          hebDate.innerText = moment(row.gregorianDate, "YYYY-MM-DD").format(
            "D MMMM"
          );
        }
        hebDate.style.color = "#1e88e5";
        hebDate.style.fontWeight = "600";
        hebDate.style.marginTop = "4px";
        td.appendChild(hebDate);
        if (isSaturday) {
          const parsha = document.createElement("div");
          parsha.innerText = row.parsha;
          parsha.style.marginTop = "8px";
          parsha.style.color = "#000b87";
          parsha.style.fontWeight = "600";
          td.appendChild(parsha);
        }
        if (row.holiday) {
          const holiday = document.createElement("div");
          holiday.innerText = row.holiday;
          holiday.style.marginTop = "8px";
          holiday.style.color = "#d32f2f";
          holiday.style.fontWeight = "700";
          td.appendChild(holiday);
        }
        if (row.zmanim) {
          const zmanim = document.createElement("div");
          zmanim.innerText = row.zmanim;
          zmanim.style.marginTop = "8px";
          zmanim.style.color = "#4b5563";
          zmanim.style.fontWeight = "600";
          td.appendChild(zmanim);
        }
      } else {
        const outDay = document.createElement("div");
        outDay.innerText = String(cellDate.date());
        outDay.style.fontSize = "32px";
        outDay.style.color = "#9ca3af";
        td.appendChild(outDay);
      }

      tr.appendChild(td);
      cursor.add(1, "day");
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrapper.appendChild(table);

  return wrapper;
}

export async function exportCalendarToPdf(rows, options) {
  const { startDate, endDate, titleText, type } = options;
  const calendarHtml = createPdfCalendarHtml(
    rows,
    startDate,
    endDate,
    titleText,
    type
  );
  document.body.appendChild(calendarHtml);

  try {
    const canvas = await html2canvas(calendarHtml, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("landscape", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
    const renderWidth = imgWidth * ratio;
    const renderHeight = imgHeight * ratio;
    const offsetX = (pageWidth - renderWidth) / 2;
    const offsetY = (pageHeight - renderHeight) / 2;

    pdf.addImage(imgData, "PNG", offsetX, offsetY, renderWidth, renderHeight);
    const pdfBlobUrl = pdf.output("bloburl");
    window.open(pdfBlobUrl, "_blank");
  } finally {
    document.body.removeChild(calendarHtml);
  }
}

export const EXCEL_EXPORT_FIELD_DEFINITIONS = [
  { key: "Date", value: (row) => row.gregorianDate, isDefault: true },
  { key: "Day", value: (row) => row.gregorianDay, isDefault: false },
  {
    key: "Day in Week Name",
    value: (row) => row.dayInWeekNameLong,
    isDefault: false,
  },
  {
    key: "Day in Week Short Name",
    value: (row) => row.dayInWeekNameShort,
    isDefault: false,
  },
  {
    key: "Day in Week Number",
    value: (row) => row.dayInWeekNum,
    isDefault: false,
  },
  {
    key: "Month Name",
    value: (row) => row.gregorianMonthName,
    isDefault: false,
  },
  {
    key: "Month Number",
    value: (row) => row.gregorianMonthNum,
    isDefault: false,
  },
  { key: "Year", value: (row) => row.gregorianYear, isDefault: false },
  {
    key: "Jewish Date",
    value: (row) =>
      row.hebrewYear ? `${row.hebrewDate} ${row.hebrewYear}` : row.hebrewDate,
    isDefault: true,
  },
  { key: "Jewish Day", value: (row) => row.hebrewDay, isDefault: false },
  {
    key: "Jewish Day in Week",
    value: (row) => row.hebrewDayInWeekNameLong,
    isDefault: false,
  },
  {
    key: "Jewish Day in Week Short",
    value: (row) => row.hebrewDayInWeekNameShort,
    isDefault: false,
  },
  {
    key: "Jewish Day in Week Yiddish",
    value: (row) => row.hebrewDayInWeekNameYiddish,
    isDefault: false,
  },
  {
    key: "Jewish Month",
    value: (row) => row.hebrewMonthName,
    isDefault: false,
  },
  { key: "Jewish Year", value: (row) => row.hebrewYear, isDefault: false },
  {
    key: "Jewish Day Number",
    value: (row) => row.hebrewDayNum,
    isDefault: false,
  },
  {
    key: "Jewish Month Number",
    value: (row) => row.hebrewMonthNum,
    isDefault: false,
  },
  {
    key: "Jewish Year Number",
    value: (row) => row.hebrewYearNum,
    isDefault: true,
  },
  { key: "Holiday", value: (row) => row.holiday, isDefault: true },
  { key: "Zmanim", value: (row) => row.zmanim, isDefault: true },
  { key: "Parsha", value: (row) => row.parsha, isDefault: true },
];

export function exportCalendarToExcel(rows, dateRange, selectedFields) {
  const selectedFieldsSet = new Set(selectedFields);
  const { startDate, endDate } = dateRange;
  const exportRows = rows.map((row) =>
    EXCEL_EXPORT_FIELD_DEFINITIONS.filter((field) =>
      selectedFieldsSet.has(field.key)
    ).reduce((acc, field) => {
      acc[field.key] = field.value(row);
      return acc;
    }, {})
  );

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Calendar");
  XLSX.writeFile(workbook, `calendar-${startDate}-to-${endDate}.xlsx`);
}
